// src/components/Creators/Creators.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import API from '../config';
import {
  Box,
  Button,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Stack,
  Chip,
  Rating,
  Paper,
  InputAdornment,
  Divider,
  Fade,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  Search as SearchIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

const recommendationOptions = [
  { value: null, label: 'All Recommendations' },
  { value: 50, label: '50%+' },
  { value: 75, label: '75%+' },
  { value: 90, label: '90%+' },
];

const spendOptions = [
  { value: '', label: 'All Spend Ranges' },
  { value: 'Under $10', label: 'Under $10' },
  { value: '$10-$25', label: '$10–25' },
  { value: '$25-$50', label: '$25–50' },
  { value: '$50+', label: '$50+' },
];

const sortOptions = [
  { value: 'recDesc', label: 'Most Recommended' },
  { value: 'reviewsDesc', label: 'Most Reviewed' },
  { value: 'spendAsc', label: 'Lowest Average Spend' },
];

export default function Creators() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const theme = useTheme();

  // State
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [recFilter, setRecFilter] = useState(null);
  const [spendFilter, setSpendFilter] = useState('');
  const [sortOption, setSortOption] = useState('recDesc');
  const [activeFilters, setActiveFilters] = useState(0);

  // Fetch creators
  useEffect(() => {
    axios
      .get(`${API}/api/creators`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((resp) => setCreators(resp.data.creators || []))
      .catch((err) => {
        console.error(err);
        setError('Failed to load creators.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (searchTerm) count++;
    if (recFilter !== null) count++;
    if (spendFilter) count++;
    setActiveFilters(count);
  }, [searchTerm, recFilter, spendFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setRecFilter(null);
    setSpendFilter('');
  };

  // Enhance with stats
  const enhanced = useMemo(() => {
    return creators.map((c) => {
      const reviews = Array.isArray(c.reviews) ? c.reviews : [];
      const total = reviews.length;
      const recCount = reviews.filter((r) => r.recommend).length;
      const recPct = total ? (recCount / total) * 100 : 0;

      // avg spend
      const spentNums = reviews
        .map((r) => {
          const f = r.spent || '';
          if (/^Under\s*\$10/.test(f)) return 5;
          if (/\+\s*$/.test(f)) return parseFloat(f.replace(/[\$+]/g, ''));
          const parts = f.replace(/\$/g, '').split(/[–-]/).map((n) => parseFloat(n));
          return parts.length === 2 ? (parts[0] + parts[1]) / 2 : NaN;
        })
        .filter((n) => !isNaN(n));
      const avgSpent = spentNums.length
        ? spentNums.reduce((a, b) => a + b, 0) / spentNums.length
        : 0;

      // avg rating
      const sumCQ = reviews.reduce((sum, r) => sum + (Number(r.contentQuality) || 0), 0);
      const averageRating = total ? sumCQ / total : 0;

      return {
        ...c,
        recPct,
        avgSpent,
        reviewCount: total,
        averageRating,
      };
    });
  }, [creators]);

  // Apply search, filters, sort
  const filtered = useMemo(() => {
    return enhanced
      .filter((c) => c.username?.toLowerCase().includes(searchTerm.toLowerCase()))
      .filter((c) => recFilter == null || c.recPct >= recFilter)
      .filter((c) => {
        if (!spendFilter) return true;
        const f = spendFilter;
        if (f === 'Under $10') return c.avgSpent < 10;
        if (f === '$50+') return c.avgSpent >= 50;
        const [low, high] = f.replace(/\$/g, '').split(/[–-]/).map(Number);
        return c.avgSpent >= low && c.avgSpent < high;
      })
      .sort((a, b) => {
        switch (sortOption) {
          case 'recDesc':
            return b.recPct - a.recPct;
          case 'reviewsDesc':
            return b.reviewCount - a.reviewCount;
          case 'spendAsc':
            return a.avgSpent - b.avgSpent;
          default:
            return 0;
        }
      });
  }, [enhanced, searchTerm, recFilter, spendFilter, sortOption]);

  // Loading states
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
          Loading creators...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Discover Top Creators
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Browse and filter through our curated list of creators, rated by real subscribers.
        </Typography>
      </Box>

      {/* Filters */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(
            theme.palette.background.paper,
            0.98
          )})`,
          backdropFilter: 'blur(8px)',
        }}
      >
        <Grid container spacing={3}>
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search by name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Filters */}
          <Grid item xs={12} sm={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ThumbUp fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                  Recommend %
                </Box>
              </InputLabel>
              <Select
                label="Recommend %"
                value={recFilter ?? ''}
                onChange={(e) => setRecFilter(e.target.value ? Number(e.target.value) : null)}
                sx={{ minWidth: '180px' }}
              >
                {recommendationOptions.map((o) => (
                  <MenuItem key={o.value ?? 'all'} value={o.value ?? ''}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                  Spend Range
                </Box>
              </InputLabel>
              <Select
                label="Spend Range"
                value={spendFilter}
                onChange={(e) => setSpendFilter(e.target.value)}
                sx={{ minWidth: '180px' }}
              >
                {spendOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SortIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                  Sort By
                </Box>
              </InputLabel>
              <Select
                label="Sort By"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                {sortOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Active filters & clear button */}
          {activeFilters > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {activeFilters} active filter{activeFilters !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Chip
                  label="Clear All Filters"
                  size="small"
                  onClick={clearFilters}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Results count */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" fontWeight={500}>
          {filtered.length} Creator{filtered.length !== 1 ? 's' : ''} Found
        </Typography>
      </Box>

      {/* Creator cards */}
      <Grid container spacing={3}>
        {filtered.length === 0 ? (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.7),
              }}
            >
              <Typography variant="h6" gutterBottom>
                No creators match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Try adjusting your search criteria or clearing some filters.
              </Typography>
              <Chip
                label="Clear All Filters"
                onClick={clearFilters}
                color="primary"
                sx={{ mt: 1 }}
              />
            </Paper>
          </Grid>
        ) : (
          filtered.map((creator, index) => (
            <Fade key={creator.id} in={true} timeout={300 + index * 100}>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 8,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: 140,
                      backgroundImage: `url(${creator.bannerImage || '/default-banner.jpg'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                      },
                    }}
                  />

                  <CardContent sx={{ position: 'relative', flexGrow: 1, pt: 0 }}>
                    <Box
                      sx={{
                        mt: -5,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={creator.profilePicture || '/default-profile.png'}
                        alt={creator.username}
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          border: `3px solid ${theme.palette.background.paper}`,
                          boxShadow: 3,
                        }}
                      />

                      <Chip
                        icon={
                          creator.recPct >= 75 ? (
                            <ThumbUp fontSize="small" />
                          ) : creator.recPct >= 50 ? (
                            <ThumbUp fontSize="small" />
                          ) : (
                            <ThumbDown fontSize="small" />
                          )
                        }
                        label={`${Math.round(creator.recPct)}% Recommend`}
                        color={
                          creator.recPct >= 75
                            ? 'success'
                            : creator.recPct >= 50
                            ? 'primary'
                            : 'error'
                        }
                        size="small"
                        sx={{ height: 28 }}
                      />
                    </Box>

                    <Typography variant="h6" component="h2" gutterBottom>
                      {creator.username}
                    </Typography>

                    <Stack spacing={1.5} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Rating:
                        </Typography>
                        <Rating
                          value={creator.averageRating}
                          precision={0.5}
                          size="small"
                          readOnly
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {creator.averageRating.toFixed(1)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Reviews:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {creator.reviewCount}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Avg Spend:
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {creator.avgSpent > 0 ? `$${creator.avgSpent.toFixed(2)}` : 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        component={RouterLink}
                        to={`/creator/${creator.id}`}
                        variant="contained"
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        View Profile
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Fade>
          ))
        )}
      </Grid>
    </Container>
  );
}
